import React, { useState, useContext, useRef } from 'react';

import { useNavigate } from 'react-router-dom';
import { useViewAs } from 'hooks/useViewAs';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Select,
  Input,
  IconButton,
  Tooltip,
  Button,
  Flex,
  Stack,
  Collapse,
  HStack,
  Switch,
  Spinner,
  Checkbox,
  Badge,
  useColorMode,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
} from '@chakra-ui/react';

import {
  SearchIcon,
  SmallCloseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';

import Card from 'components/card/Card';
import Paginate from 'components/paginate/Paginate';

import { apiGetWIPList, apiArchiveWIPPolicies } from 'api';

import { AuthContext } from 'contexts/AuthContext';

import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  WIP_STATUS_EXPIRED,
  WIP_STATUS_COMPLETED,
  WIP_STATUS_BINDER_UPLOADED,
  WIP_STATUS_APPEAL_REJECTED,
  WIP_STATUS_BARA_DECLINED,
} from 'variables/constants';
import type { APIPaginatedResponse, APIWIPItem } from 'types/api.types';
import { useUrlFilters } from 'hooks/useUrlFilters';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

// Terminal / declined WIP statuses that can never be "overdue" — the item is done,
// expired, or a declined dead-end, so it carries no pending action. Used to suppress
// the OVERDUE tag + row highlight even when the backend marks the item is_stale.
// NOTE: Pending Upload is deliberately NOT here — the binder upload is still owed,
// so a stale pending-upload item is genuinely overdue.
const OVERDUE_EXCLUDED_WIP_STATUSES = new Set<number>([
  WIP_STATUS_COMPLETED,
  WIP_STATUS_EXPIRED,
  WIP_STATUS_BINDER_UPLOADED,
  WIP_STATUS_APPEAL_REJECTED,
  WIP_STATUS_BARA_DECLINED,
]);

const isOverdueItem = (item: APIWIPItem): boolean =>
  !!item.is_stale &&
  !item.is_new &&
  !OVERDUE_EXCLUDED_WIP_STATUSES.has(item.wip_status as number);

const WIP_FILTER_KEYS = [
  'wip_status',
  'work_type',
  'submission_type',
  'carrier',
  'client_name',
  'sort_field',
  'sort_direction',
] as const;

const WIP_DEFAULTS: Record<string, string> = {
  sort_field: 'effective_date',
  sort_direction: 'desc',
};

const WorkInProgressView = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    getFilter,
    setFilter,
    setFilters,
    setFiltersAndPage,
    page,
    setPage,
    resetAll,
    searchParams,
  } = useUrlFilters(WIP_FILTER_KEYS, WIP_DEFAULTS);

  const queryClient = useQueryClient();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const {
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useDisclosure();

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [carrierInputValue, setCarrierInputValue] = useState('');
  const [clientNameInputValue, setClientNameInputValue] = useState('');
  const [mpViewEnabled, setMpViewEnabled] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());

  const toggleSelection = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedUuids(new Set());
  };

  const archiveMutation = useMutation({
    mutationFn: (uuids: string[]) => apiArchiveWIPPolicies(uuids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wip-list'] });
      exitSelectMode();
      onArchiveClose();
      toast({
        title: 'Archived successfully',
        status: 'success',
        duration: 3000,
      });
    },
    onError: () => {
      toast({ title: 'Failed to archive', status: 'error', duration: 3000 });
    },
  });

  const handleArchiveConfirm = () => {
    archiveMutation.mutate(Array.from(selectedUuids));
  };

  const statusFilter = getFilter('wip_status');
  const workTypeFilter = getFilter('work_type');
  const submissionTypeFilter = getFilter('submission_type');
  const carrierFilter = getFilter('carrier');
  const clientNameFilter = getFilter('client_name');
  const sortField = getFilter('sort_field') || 'effective_date';
  const sortDirection = (
    getFilter('sort_direction') === 'asc' ? 'asc' : 'desc'
  ) as 'asc' | 'desc';

  React.useEffect(() => {
    setCarrierInputValue(searchParams.get('carrier') ?? '');
    setClientNameInputValue(searchParams.get('client_name') ?? '');
  }, [searchParams]);

  const { colorMode } = useColorMode();

  const colors = {
    textColor: colorMode === 'light' ? 'secondaryGray.900' : 'white',
    borderColor: colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100',
    hoverBg: colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100',
    checkboxBorder: colorMode === 'light' ? 'gray.500' : 'whiteAlpha.500',
  } as const;

  const textColor = colors.textColor;
  const hoverBg = colors.hoverBg;
  const borderColor = colors.borderColor;

  const viewAs = useViewAs();
  const isHomepageRedesignEnabled = useFeatureFlag('enable-homepage-redesign');
  const isBaraEnabled = useFeatureFlag('enable-bara');

  const filters = React.useMemo(() => {
    const result: any = {};
    if (statusFilter) result.wip_status = parseInt(statusFilter);
    if (workTypeFilter) result.work_type = parseInt(workTypeFilter);
    if (submissionTypeFilter) result.submission_type = submissionTypeFilter;
    if (carrierFilter.trim()) result.carrier = carrierFilter.trim();
    if (clientNameFilter.trim()) result.client_name = clientNameFilter.trim();
    if (sortField) result.sort_field = sortField;
    if (sortDirection) result.sort_direction = sortDirection;
    return { ...result, ...viewAs.filterParams };
  }, [
    statusFilter,
    workTypeFilter,
    submissionTypeFilter,
    carrierFilter,
    clientNameFilter,
    sortField,
    sortDirection,
    viewAs.filterParams,
  ]);

  const { data, error, isFetching, isLoading } = useQuery<
    APIPaginatedResponse<APIWIPItem>
  >({
    queryKey: ['wip-list', page, filters],
    queryFn: () => apiGetWIPList(page + 1, filters),
  });

  const handleCarrierInputChange = (value: string) => {
    setCarrierInputValue(value);
  };

  const handleClientNameInputChange = (value: string) => {
    setClientNameInputValue(value);
  };

  const handleSearch = () => {
    setFilters({
      carrier: carrierInputValue.trim(),
      client_name: clientNameInputValue.trim(),
    });
  };

  const resetSearch = () => {
    setCarrierInputValue('');
    setClientNameInputValue('');
    resetAll();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setFiltersAndPage(
        { sort_direction: sortDirection === 'asc' ? 'desc' : 'asc' },
        0,
      );
    } else {
      setFiltersAndPage({ sort_field: field, sort_direction: 'asc' }, 0);
    }
  };

  // Data is already sorted by backend, no need for frontend sorting
  const sortedData = data?.results || [];

  const expiredItems = sortedData.filter(
    (item) => item.wip_status === WIP_STATUS_EXPIRED && !item.is_archived,
  );
  const allExpiredSelected =
    expiredItems.length > 0 &&
    expiredItems.every((item) => item.uuid && selectedUuids.has(item.uuid));

  const toggleAllExpired = () => {
    if (allExpiredSelected) {
      setSelectedUuids(new Set());
    } else {
      setSelectedUuids(
        new Set(expiredItems.map((item) => item.uuid!).filter(Boolean)),
      );
    }
  };

  if (error) {
    return (
      <Card>
        <Box p="20px">
          <Text color="red.500">Error loading WIP items</Text>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Work In Progress
        </Text>
        <HStack spacing={4}>
          {user.role === ROLE_MANAGER && (
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="500">
                MP View
              </Text>
              <Switch
                colorScheme="brand"
                size="md"
                isChecked={mpViewEnabled}
                onChange={(e) => setMpViewEnabled(e.target.checked)}
              />
            </HStack>
          )}
          {!selectMode && expiredItems.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectMode(true)}
            >
              Select
            </Button>
          )}
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {selectMode && (
        <Flex
          px="20px"
          mb="16px"
          align="center"
          justify="space-between"
          bg={colorMode === 'light' ? 'gray.50' : 'whiteAlpha.50'}
          py="10px"
          mx="20px"
          borderRadius="md"
        >
          <Text fontSize="sm" color={textColor} fontWeight="500">
            {selectedUuids.size === 0
              ? 'Select expired items to archive'
              : `${selectedUuids.size} item${selectedUuids.size > 1 ? 's' : ''} selected`}
          </Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="red"
              onClick={onArchiveOpen}
              isDisabled={selectedUuids.size === 0}
              isLoading={archiveMutation.isPending}
            >
              Archive{selectedUuids.size > 0 ? ` (${selectedUuids.size})` : ''}
            </Button>
            <Button size="sm" variant="ghost" onClick={exitSelectMode}>
              Cancel
            </Button>
          </HStack>
        </Flex>
      )}

      <Collapse in={showSearchBar} animateOpacity>
        <Box px="20px" mb="20px">
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <Select
                variant="main"
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(e) => setFilter('wip_status', e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="1">Validations in Progress</option>
                <option value="2">Appeal Submitted</option>
                <option value="3">Appeal Approved</option>
                <option value="4">Appeal Rejected</option>
                <option value="5">Application in Progress</option>
                <option value="6">Application Created</option>
                <option value="7">Signing: Awaiting Broker</option>
                <option value="8">Signing: Send for Client</option>
                <option value="10">Expired</option>
                {isBaraEnabled && (
                  <optgroup label="── After-Hours Referral ──">
                    <option value="13">
                      Referral submitted to MP/Delegate
                    </option>
                    <option value="14">
                      Referral under review with MP/Delegate
                    </option>
                    <option value="15">
                      Referral — additional information required
                    </option>
                    <option value="16">Referral approved by MP/Delegate</option>
                    <option value="17">Referral declined by MP/Delegate</option>
                  </optgroup>
                )}
              </Select>
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="All Work Types"
                value={workTypeFilter}
                onChange={(e) => setFilter('work_type', e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="1">New Business</option>
                <option value="2">Remarket</option>
                {/* <option value="3">Policy Change</option> */}
              </Select>
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="All Submission Types"
                value={submissionTypeFilter}
                onChange={(e) => setFilter('submission_type', e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="Auto">Auto</option>
                <option value="Home">Home</option>
                <option value="Life">Life</option>
                <option value="Non-Ontario Auto">Non-Ontario Auto</option>
                <option value="MGA/Specialty">MGA/Specialty</option>
                <option value="MGA/Specialty Home">MGA/Specialty Home</option>
                <option value="Commercial Property & Casualty">
                  Commercial Property & Casualty
                </option>
                <option value="Commercial Auto">Commercial Auto</option>
              </Select>
            </Box>

            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by carrier"
                value={carrierInputValue}
                onChange={(e) => handleCarrierInputChange(e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </Box>

            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by client name"
                value={clientNameInputValue}
                onChange={(e) => handleClientNameInputChange(e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
                isLoading={isFetching || isLoading}
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
                isLoading={isFetching || isLoading}
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.700">
          <Thead>
            <Tr>
              {selectMode && (
                <Th borderColor={borderColor} color="gray.400" w="40px" px={2}>
                  <Checkbox
                    isChecked={allExpiredSelected}
                    isIndeterminate={
                      selectedUuids.size > 0 && !allExpiredSelected
                    }
                    onChange={toggleAllExpired}
                    isDisabled={expiredItems.length === 0}
                    colorScheme="brand"
                    borderColor={colors.checkboxBorder}
                  />
                </Th>
              )}
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Client Name</Text>
                  <Tooltip label="Sort by Client Name">
                    <IconButton
                      aria-label="Sort by Client Name"
                      icon={
                        sortField === 'client_name' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('client_name')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Created Date</Text>
                  <Tooltip label="Sort by Created Date">
                    <IconButton
                      aria-label="Sort by Created Date"
                      icon={
                        sortField === 'created_at' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('created_at')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Effective Date</Text>
                  <Tooltip label="Sort by Effective Date">
                    <IconButton
                      aria-label="Sort by Effective Date"
                      icon={
                        sortField === 'effective_date' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('effective_date')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Submission Type</Text>
                  <Tooltip label="Sort by Submission Type">
                    <IconButton
                      aria-label="Sort by Submission Type"
                      icon={
                        sortField === 'submission_type' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('submission_type')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Work Type</Text>
                  <Tooltip label="Sort by Work Type">
                    <IconButton
                      aria-label="Sort by Work Type"
                      icon={
                        sortField === 'work_type_display' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('work_type_display')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Premium</Text>
                  <Tooltip label="Sort by Premium">
                    <IconButton
                      aria-label="Sort by Premium"
                      icon={
                        sortField === 'premium' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('premium')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Carrier</Text>
                  <Tooltip label="Sort by Carrier">
                    <IconButton
                      aria-label="Sort by Carrier"
                      icon={
                        sortField === 'carrier_name' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('carrier_name')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              {(user.role === ROLE_ADMIN || mpViewEnabled) && (
                <Th borderColor={borderColor} color="gray.400">
                  <HStack spacing={1}>
                    <Text color="gray.400">Broker</Text>
                    <Tooltip label="Sort by Broker">
                      <IconButton
                        aria-label="Sort by Broker"
                        icon={
                          sortField === 'broker_name' ? (
                            sortDirection === 'asc' ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          ) : (
                            <ChevronUpIcon />
                          )
                        }
                        size="xs"
                        variant="ghost"
                        onClick={() => handleSort('broker_name')}
                      />
                    </Tooltip>
                  </HStack>
                </Th>
              )}
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Status</Text>
                  <Tooltip label="Sort by Status">
                    <IconButton
                      aria-label="Sort by Status"
                      icon={
                        sortField === 'wip_status_display' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('wip_status_display')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={selectMode ? 10 : 9} textAlign="center">
                  <Flex align="center" justify="center" mt="20px">
                    <Spinner size="sm" mr="10px" /> Loading policies...
                  </Flex>
                </Td>
              </Tr>
            )}

            {sortedData.map((item) => (
              <Tr
                key={item.id}
                cursor="pointer"
                onClick={() => {
                  navigate(
                    `/admin/policies/submission/${item.submission_id}/${item.uuid}`,
                  );
                }}
                _hover={{
                  bg: hoverBg,
                }}
                bg={
                  isHomepageRedesignEnabled && item.is_new
                    ? 'blue.50'
                    : isHomepageRedesignEnabled && isOverdueItem(item)
                      ? 'orange.50'
                      : undefined
                }
                _dark={{
                  bg:
                    isHomepageRedesignEnabled && item.is_new
                      ? 'blue.900'
                      : isHomepageRedesignEnabled && isOverdueItem(item)
                        ? 'orange.900'
                        : undefined,
                }}
              >
                {selectMode && (
                  <Td px={2} onClick={(e) => e.stopPropagation()}>
                    {item.wip_status === WIP_STATUS_EXPIRED &&
                    item.uuid &&
                    !item.is_archived ? (
                      <Checkbox
                        isChecked={selectedUuids.has(item.uuid)}
                        onChange={() => toggleSelection(item.uuid!)}
                        colorScheme="brand"
                        borderColor={colors.checkboxBorder}
                      />
                    ) : (
                      <Box w="20px" />
                    )}
                  </Td>
                )}
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.client_name}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.created_at_formatted}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.effective_date_formatted || '-'}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.submission_type === 'Life' && item.life_product
                        ? `Life/${item.life_product}`
                        : item.submission_type || '-'}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.work_type_display}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.premium ? `$${item.premium.toFixed(2)}` : '-'}
                    </Text>
                  </Flex>
                </Td>
                <Td>
                  <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.carrier_name || '-'}
                    </Text>
                  </Flex>
                </Td>
                {(user.role === ROLE_ADMIN || mpViewEnabled) && (
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {item.broker_name || '-'}
                      </Text>
                    </Flex>
                  </Td>
                )}
                <Td>
                  <Flex align="center" gap={2} flexWrap="wrap">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.wip_status_display}
                    </Text>
                    {item.is_archived && (
                      <Badge colorScheme="gray" fontSize="xs">
                        Archived
                      </Badge>
                    )}
                    {isHomepageRedesignEnabled && item.is_new && (
                      <Badge colorScheme="blue" fontSize="9px">
                        NEW
                      </Badge>
                    )}
                    {isHomepageRedesignEnabled && isOverdueItem(item) && (
                      <Badge colorScheme="orange" fontSize="9px">
                        OVERDUE
                      </Badge>
                    )}
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {data && data?.results?.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No work in progress items found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />

      <AlertDialog
        isOpen={isArchiveOpen}
        leastDestructiveRef={cancelRef}
        onClose={onArchiveClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Archive{' '}
              {selectedUuids.size === 1
                ? 'Submission'
                : `${selectedUuids.size} Submissions`}
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to archive{' '}
              {selectedUuids.size === 1
                ? 'this expired submission'
                : `these ${selectedUuids.size} expired submissions`}
              ? They will be removed from your WIP list.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onArchiveClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleArchiveConfirm}
                ml={3}
                isLoading={archiveMutation.isPending}
              >
                Archive
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Card>
  );
};

export default WorkInProgressView;
